import { ConfigProvider, App as AntApp, theme } from 'antd';
import enUS from 'antd/locale/en_US';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { AuthProvider } from '@/lib/auth';
import { RealtimeProvider } from '@/lib/realtime/RealtimeProvider';

// TanStack Query owns server state (02 §6). Zustand is reserved for genuinely
// local UI state — never for cached server data, which would create a second
// source of truth for values subject to field-level masking.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry an authorisation failure: the answer will not change, and
        // repeated attempts pollute the audit trail with phantom access events.
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Ant Design v5 design tokens (decisions.md D-003).
const themeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: '#1b5e9c',
    borderRadius: 6,
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    // HR registers are long; compact rows keep more of a page visible.
    Table: { cellPaddingBlock: 10 },
  },
};

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ConfigProvider locale={enUS} theme={themeConfig}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RealtimeProvider>{children}</RealtimeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
